import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { balanceEquation, type BalanceResult } from "@/data/equationBalancer";
import { ArrowRight, CheckCircle2, XCircle } from "@/lib/icons";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";

const examples = [
  "H2 + O2 -> H2O",
  "Fe + O2 -> Fe2O3",
  "CH4 + O2 -> CO2 + H2O",
  "Al + HCl -> AlCl3 + H2",
  "KMnO4 + HCl -> KCl + MnCl2 + H2O + Cl2",
  "C3H8 + O2 -> CO2 + H2O",
];

export default function EquationBalancer() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState<BalanceResult | null>(null);
  const [searchParams] = useSearchParams();

  // Pre-fill from ?eq= so other tools (e.g. Lab) can deep-link an equation in.
  useEffect(() => {
    const eq = searchParams.get("eq");
    if (eq) {
      setInput(eq);
      setResult(balanceEquation(eq));
    }
    // Only on first mount - subsequent edits should not be overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBalance = () => {
    if (!input.trim()) return;
    setResult(balanceEquation(input));
  };

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemical Equation Balancer — ElementPad" description="Balance chemical equations instantly with step-by-step coefficients. Free online tool for high school and intro college chemistry." path="/tools/equation-balancer" jsonLd={{"@context":"https://schema.org","@type":"SoftwareApplication","name":"Equation Balancer","applicationCategory":"EducationalApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}}} />
      <AppHeader />
      <Main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Equation Balancer</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Enter a chemical equation and get balanced coefficients with step-by-step work.
        </p>

        {/* Input */}
        <div className="flex gap-2 mb-4">
          <label htmlFor="equation-input" className="sr-only">Chemical equation</label>
          <input
            id="equation-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleBalance()}
            placeholder="e.g. H2 + O2 -> H2O"
            className="flex-1 h-11 rounded-lg border border-input bg-tile-bg px-4 font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            onClick={handleBalance}
            className="h-11 px-6 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Balance
          </button>
        </div>

        {/* Examples */}
        <div className="flex flex-wrap gap-2 mb-8">
          <span className="text-xs text-muted-foreground pt-1">Try:</span>
          {examples.map((ex) => (
            <button
              key={ex}
              onClick={() => {
                setInput(ex);
                setResult(balanceEquation(ex));
              }}
              className="text-xs px-2.5 py-1 rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-mono"
            >
              {ex}
            </button>
          ))}
        </div>

        {/* Result */}
        {result && (
          <div className="space-y-4">
            {/* Balanced equation */}
            <div className={`rounded-lg border p-4 ${result.success ? "border-primary/30 bg-primary/5" : "border-destructive/30 bg-destructive/5"}`}>
              <div className="flex items-center gap-2 mb-2">
                {result.success ? (
                  <CheckCircle2 size={16} className="text-primary" />
                ) : (
                  <XCircle size={16} className="text-destructive" />
                )}
                <span className="text-sm font-medium text-foreground">
                  {result.success ? "Balanced Equation" : result.error || "Could not balance"}
                </span>
              </div>
              {result.balanced && (
                <p className="font-mono text-lg text-foreground">{result.balanced}</p>
              )}
            </div>

            {/* Steps */}
            {result.steps.length > 0 && (
              <div className="rounded-lg border border-border bg-tile-bg p-4">
                <h3 className="text-sm font-medium text-foreground mb-3">Solution Steps</h3>
                <ol className="space-y-2">
                  {result.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground font-mono text-xs mt-0.5 min-w-[1.5rem]">
                        {i + 1}.
                      </span>
                      <span className="text-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Coefficients */}
            {result.success && (
              <div className="rounded-lg border border-border bg-tile-bg p-4">
                <h3 className="text-sm font-medium text-foreground mb-3">Coefficients</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {result.reactants.map((r, i) => (
                    <span key={`r-${i}`} className="flex items-center gap-1">
                      {i > 0 && <span className="text-muted-foreground">+</span>}
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted">
                        <span className="font-mono font-bold text-primary">{result.coefficients[i]}</span>
                        <span className="font-mono text-sm text-foreground">{r}</span>
                      </span>
                    </span>
                  ))}
                  <ArrowRight size={16} className="text-muted-foreground" />
                  {result.products.map((p, i) => (
                    <span key={`p-${i}`} className="flex items-center gap-1">
                      {i > 0 && <span className="text-muted-foreground">+</span>}
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted">
                        <span className="font-mono font-bold text-primary">{result.coefficients[result.reactants.length + i]}</span>
                        <span className="font-mono text-sm text-foreground">{p}</span>
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Atom Count Verification */}
            {result.success && result.atomCounts.length > 0 && (
              <div className="rounded-lg border border-border bg-tile-bg p-4">
                <h3 className="text-sm font-medium text-foreground mb-3">Atom Count Verification</h3>
                <div className="grid grid-cols-3 gap-0 text-sm">
                  <div className="p-2 text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-border">Element</div>
                  <div className="p-2 text-xs text-muted-foreground font-medium uppercase tracking-wider text-center border-b border-border">Reactants</div>
                  <div className="p-2 text-xs text-muted-foreground font-medium uppercase tracking-wider text-center border-b border-border">Products</div>
                  {result.atomCounts.map((ac) => (
                    <React.Fragment key={ac.element}>
                      <div className="p-2 font-mono font-semibold text-foreground border-b border-border last:border-0">{ac.element}</div>
                      <div className={`p-2 font-mono text-center border-b border-border ${ac.balanced ? "text-primary" : "text-destructive"}`}>{ac.reactants}</div>
                      <div className={`p-2 font-mono text-center border-b border-border ${ac.balanced ? "text-primary" : "text-destructive"}`}>{ac.products}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8">
          <WorkedExample
            title="Balancing C₃H₈ + O₂ → CO₂ + H₂O (combustion of propane)"
            problem="Balance the combustion of propane: C₃H₈ + O₂ → CO₂ + H₂O."
            steps={[
              {
                label: "Step 1: Balance carbon first",
                explain:
                  "Propane has 3 carbons, so you need 3 CO₂ on the right.",
                formula: "C₃H₈ + O₂ → 3 CO₂ + H₂O",
              },
              {
                label: "Step 2: Balance hydrogen next",
                explain:
                  "Propane has 8 H. Each H₂O has 2 H, so you need 4 H₂O.",
                formula: "C₃H₈ + O₂ → 3 CO₂ + 4 H₂O",
              },
              {
                label: "Step 3: Count oxygen on the RIGHT",
                explain:
                  "3 CO₂ = 6 O. 4 H₂O = 4 O. Total = 10 O atoms on the right.",
                formula: "Right side: 6 + 4 = 10 O",
              },
              {
                label: "Step 4: Balance oxygen LAST (it's only in O₂ on the left)",
                explain:
                  "Save oxygen for last because it appears in two products. 10 O atoms ÷ 2 = 5 O₂ molecules.",
                formula: "C₃H₈ + 5 O₂ → 3 CO₂ + 4 H₂O",
              },
              {
                label: "Step 5: Verify by counting both sides",
                explain:
                  "C: 3 = 3 ✓   H: 8 = 8 ✓   O: 10 = 10 ✓",
              },
            ]}
            answer="C₃H₈ + 5 O₂ → 3 CO₂ + 4 H₂O"
          />
        </div>
      </Main>
    </div>
  );
}
